import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Buildings, CheckCircle, Receipt } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import api, { formatApiError } from "@/lib/api";
import { INVOICE_STATUS_LABELS, INVOICE_STATUS_COLORS } from "@/lib/constants";

const rupee = (n) => `₹${(Number(n) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function PayInvoice() {
  const { id } = useParams();
  const token = new URLSearchParams(window.location.search).get("token") || "";
  const [inv, setInv] = useState(null);
  const [error, setError] = useState("");
  const [paying, setPaying] = useState(false);
  const [payEnabled, setPayEnabled] = useState(true);

  const load = () => api.get(`/public/invoices/${id}?token=${encodeURIComponent(token)}`).then((r) => setInv(r.data)).catch((e) => setError(formatApiError(e.response?.data?.detail) || "Invoice not found"));

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get("razorpay_payment_link_status");
    if (status) {
      api.post(`/public/invoices/${id}/confirm`, {
        razorpay_payment_id: params.get("razorpay_payment_id"),
        razorpay_payment_link_id: params.get("razorpay_payment_link_id"),
        razorpay_payment_link_reference_id: params.get("razorpay_payment_link_reference_id"),
        razorpay_payment_link_status: status,
        razorpay_signature: params.get("razorpay_signature"),
        token,
      }).catch(() => {}).finally(() => {
        window.history.replaceState({}, "", `/pay/${id}${token ? `?token=${token}` : ""}`);
        load();
      });
    } else {
      load();
    }
    api.get("/payments/config").then((r) => setPayEnabled(!!r.data.enabled)).catch(() => {});
    /* eslint-disable-next-line */
  }, [id]);

  const pay = async () => {
    setPaying(true);
    try {
      const { data } = await api.post(`/public/invoices/${id}/checkout`, { origin_url: window.location.origin, token });
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Online payment is being set up. Please contact Aakriti Associates to pay.");
    } finally { setPaying(false); }
  };

  return (
    <div className="min-h-screen bg-[#0B0C10] text-white noise-overlay">
      <header className="backdrop-blur-xl bg-black/40 border-b border-white/10">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5"><div className="w-9 h-9 rounded-md bg-white/10 flex items-center justify-center"><Buildings weight="fill" className="text-accent" size={20} /></div><span className="font-display font-bold text-lg">Aakriti Associates</span></Link>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-6 py-16">
        {error && !inv && <div className="text-rose-300" data-testid="pay-error">{error}</div>}
        {inv && (
          <Card className="p-8 bg-white text-foreground" data-testid="pay-invoice">
            <div className="flex items-start justify-between border-b border-border pb-5">
              <div>
                <div className="text-xs tracking-[0.15em] uppercase text-muted-foreground flex items-center gap-1.5"><Receipt size={15} className="text-accent" /> Invoice</div>
                <div className="font-mono text-lg text-primary mt-1">{inv.invoice_number}</div>
                <div className="text-sm text-muted-foreground mt-1">Billed to {inv.bill_to_name}</div>
              </div>
              <span className={`text-xs px-2.5 py-1 rounded border ${INVOICE_STATUS_COLORS[inv.status]}`}>{INVOICE_STATUS_LABELS[inv.status]}</span>
            </div>
            <table className="w-full text-sm my-5">
              <thead><tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b border-border"><th className="py-2">Description</th><th className="py-2 text-right">Qty</th><th className="py-2 text-right">Amount</th></tr></thead>
              <tbody>
                {(inv.line_items || []).map((it, i) => (
                  <tr key={i} className="border-b border-border"><td className="py-2">{it.description}</td><td className="py-2 text-right font-mono">{it.qty}</td><td className="py-2 text-right font-mono">{rupee((it.qty || 0) * (it.rate || 0))}</td></tr>
                ))}
              </tbody>
            </table>
            <div className="flex justify-end">
              <div className="w-56 space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-mono">{rupee(inv.subtotal)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">GST ({inv.tax_rate}%)</span><span className="font-mono">{rupee(inv.tax_amount)}</span></div>
                <div className="flex justify-between border-t border-border pt-1.5 font-bold text-primary text-base"><span>Total Due</span><span className="font-mono">{rupee(inv.total)}</span></div>
              </div>
            </div>
            <div className="mt-8">
              {inv.status === "paid" ? (
                <div className="flex items-center gap-2 text-emerald-600 font-medium" data-testid="pay-paid"><CheckCircle weight="fill" size={22} /> This invoice has been paid. Thank you!</div>
              ) : payEnabled ? (
                <>
                  <Button onClick={pay} disabled={paying} className="w-full rounded-full bg-accent hover:bg-accent/90 text-accent-foreground font-semibold h-11" data-testid="pay-now-btn">{paying ? "Redirecting…" : `Pay ${rupee(inv.total)} Online`}</Button>
                  {error && <div className="text-sm text-amber-600 mt-3 text-center" data-testid="pay-notice">{error}</div>}
                </>
              ) : (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 text-center" data-testid="pay-coming-soon">
                  Online payment is being set up. Please contact <span className="font-medium">Aakriti Associates</span> to pay this invoice.
                </div>
              )}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
